import { Component, OnDestroy, OnInit } from '@angular/core';
import { AlertsService } from '../../../common/alerts/alerts.service';
import { ScheduleService } from '../schedule.service';
import { Schedule } from '../schedule.model';
import { UserService } from '../../../common/user';
import { Appointment } from '../appointment.model';
import { NavigationService } from '../../navigation';
import { NativeAppService } from '../../../common/native-app-service/native-app.service';
import { Subscription } from 'rxjs';
import { DateChangeEvent } from '../../../common/date-picker/date-change-event';

@Component({
  selector: 'app-schedule',
  templateUrl: './schedule.component.html',
  styleUrls: ['./schedule.component.scss']
})
export class ScheduleComponent implements OnInit, OnDestroy {
  isLoading: boolean;
  currentProvider: string = null;
  currentOperator: string = null;
  schedule: any[] = [];
  fullSchedule: Schedule;
  // Has focus event subscription
  protected appFocusChangeSub: Subscription;

  constructor(
    private readonly scheduleService: ScheduleService,
    private readonly userService: UserService,
    private readonly alertsService: AlertsService,
    private readonly navigationService: NavigationService,
    private readonly nativeService: NativeAppService
  ) {}

  async ngOnInit(): Promise<void> {
    // refresh user info to know which provider to preselect
    await this.reloadUser();
    const teamMember = this.userService.getTeamMember();
    this.currentProvider = teamMember ? teamMember.bo_context : null;

    this.reloadSchedule().then(() => {
      if (this.currentProvider === null && this.currentOperator === null) {
        // if we can't choose default selection then use first operator available
        if (this.fullSchedule?.ops?.length > 0) {
          this.currentOperator = this.fullSchedule.ops[0].bo_context;
          this.applyFilters();
        }
      }
    });
    this.appFocusChangeSub = this.nativeService
      .focusChangeObservable()
      .subscribe((hasFocus) => {
        if (hasFocus) {
          this.reloadSchedule().then();
        }
      });
  }

  private toDateStr(date: Date): string {
    return date.toISOString().slice(0, 'YYYY-MM-DD'.length);
  }

  async reloadUser(): Promise<void> {
    try {
      this.isLoading = true;
      await this.userService.loadUser();
    } catch (e) {
      this.alertsService.showApiError(e);
    } finally {
      this.isLoading = false;
    }
  }

  async reloadSchedule(event?: DateChangeEvent): Promise<void> {
    const date = event?.startDate ? event.startDate : new Date();
    const dateStr = this.toDateStr(date);

    try {
      this.isLoading = true;
      this.fullSchedule = await this.scheduleService.getSchedule(dateStr);
      this.applyFilters();
    } catch (e) {
      this.alertsService.showApiError(e);
    } finally {
      this.isLoading = false;
    }
  }

  applyFilters(): void {
    if (this.currentProvider === null && this.currentOperator === null) {
      this.schedule = []; // need at least one filter
    } else {
      this.schedule = this.fullSchedule.live
        .filter((appointment: Appointment) => {
          return (
            this.currentProvider === null ||
            this.currentProvider === appointment.bo_provider_context
          );
        })
        .filter((appointment: Appointment) => {
          return (
            this.currentOperator === null ||
            this.currentOperator === appointment.bo_operatory_context
          );
        })
        .filter((appointment: Appointment) => {
          return appointment.status !== 'cancelled';
        });
    }
  }

  onProviderChange(): void {
    if (this.currentProvider === 'null') {
      this.currentProvider = null;
    }
    this.applyFilters();
  }

  onOperatorChange(): void {
    if (this.currentOperator === 'null') {
      this.currentOperator = null;
    }
    this.applyFilters();
  }

  onAppointmentClicked(appointment: Appointment): void {
    this.navigationService.showPatientInfo(appointment.patient);
  }

  onAppointmentKeyDown($event: KeyboardEvent, appointment: Appointment): void {
    if ($event.key === 'Enter') {
      this.navigationService.showPatientInfo(appointment.patient);
    }
  }

  ngOnDestroy(): void {
    if (this.appFocusChangeSub) {
      this.appFocusChangeSub.unsubscribe();
    }
  }
}
